import { Box, useTheme } from '@mui/material';
import { AgGridEvent } from 'ag-grid-community';
import _ from 'lodash';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ApplicationMimeTypesEnum } from '../../common/enums/ApplicationMimeTypesEnum';
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ActionTypeEnum, StatusEnum } from '../../common/IExportNotificationMsg';
import UUID from "../../common/utilities/Uuid";
import { ActionMenuItem, BaseActionEnum } from '../../components/action-menu/ActionMenuItem';
import { ContentContainer } from '../../components/content-container/ContentContainer';
import { ContentSection } from '../../components/content-section/ContentSection';
import { ContentView } from '../../components/content-view/ContentView';
import ContextualAppBar from '../../components/contextual-app-bar/ContextualAppBar';
import LoadingZone from '../../components/loading-zone/LoadingZone';
import NoData from '../../components/no-data/NoData';
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { useProgressContext } from '../../contexts/progress-context/ProgressContext';
import { useRouteContext } from '../../hooks/use-route-context/UseRouteContext';
import { RestError } from "../../services/rest-service/RestService";
import useUserPermissionsReportState from './UserPermissionsReportState';
import UserPermissionsReportUtils from './UserPermissionsReportUtils';
import { useQueryNotifications } from '../../queries/UseQueryNotifications';
import { useUserPermissionsReport } from '../../queries/user-role-administration/UseUserPermissionsReport';
import UserRoleAdministrationService from '../../services/user-role-administration-service/UserRoleAdministrationService';
import { ViewOptionStateEnum } from '../../components/contextual-app-bar/ViewOptionStateEnum';
import { IOrganization } from '../../services/organization-data-service/Models';
import { useUserContext } from '../../contexts/user-context/UserContext';
import { useGroup } from '../../queries/groups/UseGroup';
import { ISortParameters } from '../../common/models/ISortParameters';
import DefaultGrid from "../../components/default-grid/DefaultGrid";
import { AutoSizeStrategyEnum } from '../../components/default-grid/InternalGrid';

export default function UserPermissionsReportView() {

    const routeContext = useRouteContext();
    const { t } = useTranslation();
    const [, setAppNotificationState] = useAppNotificationContext();
    const [progressState, actionStarted] = useProgressContext();
    const [userState] = useUserContext();
    const theme = useTheme();

    const {
        state,
        setData,
        setDownloadUid,
        setOrganization,
        setGridApi,
        setActions
    } = useUserPermissionsReportState();

    const { data: group } = useQueryNotifications(useGroup)({
        customerID: userState.customer?.id as number,
        groupID: routeContext?.groupID as number
    });


    const { data, status, refetch } = useQueryNotifications(useUserPermissionsReport)({
        organizationID: state.organizationID as number,
        groupID: routeContext.groupID as number,
        enabled: state.organizationID !== undefined
    });



    // const noDataTitle = t(!state.run ? 'actions.runReport' : 'noData.title');
    // const noDataText = state.run && state.data !== undefined && state.data.rows.length === 0 ?
    //                 t('UserPermissionsReport.noData') :
    //                 !state.run ? 
    //                 t('UserPermissionsReport.runReportHint') :
    //                 undefined;


    const onExport = async (mimeType: ApplicationMimeTypesEnum) => {

        const uid = UUID.generate();
        setDownloadUid(uid);

        actionStarted({
            uid: uid,
            objectID: routeContext.groupID as number,
            objectType: eLynxObjectTypeEnum.Group,
            title: 'User Permissions Report',
            notificationMsg: {
                action: ActionTypeEnum.Download,
                notificationID: uid,
                status: StatusEnum.Started,
                statusMessage: t('progress.sendingRequest'),
                additionalInfo: '',
                blobURI: '',
                percentComplete: 0
            }
        });

        try {

            const columnState = state.gridApi?.getColumnState();

            const sortItem = columnState?.find(o => !!o.sort);


            const sortParams: ISortParameters = {
                propertyName: sortItem?.colId ?? '',
                sortAscending: (sortItem?.sort ?? 'asc') === 'asc'
            };

            await UserRoleAdministrationService.exportUserPermissionsReport(
                uid,
                state.organizationID as number,
                routeContext.groupID as number,
                mimeType,
                sortParams);

        } catch (error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }
    };


    function onExecuteAction(menuItem: ActionMenuItem) {

        const item = menuItem.action;

        switch (item) {
            case BaseActionEnum.Refresh:
                refetch();
                break;

            case BaseActionEnum.PDF:
                onExport(ApplicationMimeTypesEnum.PDF);
                break;

            case BaseActionEnum.Excel:
            //case BaseActionEnum.Export:
                onExport(ApplicationMimeTypesEnum.MSExcel);
                break;
        }
    }

    function onOrganizationSelected(item: IOrganization) {
        setOrganization(item.id);
    }

    // const onGetRowHeight = (params: RowHeightParams): number | undefined => {

    //     let approximateLinesOfText = Math.max((params.data.roleList?.length ?? 0) / 16, 2);

    //     // add room for the timestamp line
    //     ++approximateLinesOfText;

    //     return approximateLinesOfText > 3 ? approximateLinesOfText * parseFloat(theme.spacing(2.25)) : parseFloat(theme.spacing(8));

    // };

    function onGridReady(event: AgGridEvent) {
        setGridApi(event.api);
    }


    // get progress running for current object
    const progress = progressState.items.find(o => o.objectID === routeContext.groupID && o.objectType === eLynxObjectTypeEnum.Group);

    useEffect(() => {

        if (progress?.uid === state.downloadUid &&
            (progress?.notificationMsg.status === StatusEnum.Completed ||
                progress?.notificationMsg.status === StatusEnum.Failed)) {
            setDownloadUid(undefined);
        }
    }, [
        progress,
        state.downloadUid,
        setDownloadUid
    ]);

    useEffect(() => {
        if (group) {
            setOrganization(group.organizationID);
        }
    }, [
        group,
        setOrganization
    ]);


    useEffect(() => {

        if (data) {

            const cols = UserPermissionsReportUtils.createGridColumns(theme);
            const rows = UserPermissionsReportUtils.createGridRows(data);

            setData({
                cols,
                rows
            });
        }
    }, [
        data,
        setData,
        theme
    ]);


    useEffect(() => {
        const actions = _.cloneDeep(state.actions);

        const exportAction = actions.find(o => o.action === BaseActionEnum.Export);

        if (exportAction) {
            exportAction.disabled = progress && progress.uid === state.downloadUid;
        }

        // if (!_.isEqual(state.actions, actions)) {
            setActions(actions);
        // }
    }, [
        progress,
        state.actions,
        state.downloadUid,
        setActions
    ]);

    return (
        <ContentView>
            <ContextualAppBar
                hideBreadcrumb={true}
                actions={state.actions}
                viewOptionState={ViewOptionStateEnum.Hidden}
                onExecuteAction={onExecuteAction}
                showOrganizationPicker={true}
                onOrganizationSelected={onOrganizationSelected}
                organizationID={state.organizationID}
            />

            <LoadingZone isLoading={status === 'loading'}>
                <ContentContainer>
                    <ContentSection>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                flexGrow: 1,
                                margin: '1rem',
                                overflow: "hidden"
                            }}>

                            {status === 'success' && (state.data?.rows.length ?? 0) === 0 &&
                                <NoData
                                    title={t('permissionReport.nodata.title')}
                                    text={t('permissionReport.nodata.text')}
                                />
                            }

                            {state.data && state.data.rows.length > 0 &&
                                <DefaultGrid
                                    onGridReady={onGridReady}
                                    suppressColumnVirtualisation={true}
                                    suppressMultiSort={true}
                                    rowData={state.data.rows}
                                    defaultColDef={{
                                        sortable: true,
                                        filter: "agMultiColumnFilter",
                                        resizable: true,
                                        wrapHeaderText: true,
                                        // SPR 04-08-2025 - Removed autoHeight and wrapText from defaultColDef
                                        // Use multiline column type in column definitions, instead
                                        cellStyle: {
                                            wordBreak: 'auto-phrase',
                                        }
                                    }}
                                    // getRowHeight={onGetRowHeight}
                                    columnDefs={state.data.cols}
                                    autoSizeStrategy={AutoSizeStrategyEnum.None}
                                    getRowId={data => `${data.data.userID}:${data.data.group}:${data.data.permissionType}`}
                                />
                            }
                        </Box>
                    </ContentSection>
                </ContentContainer>
            </LoadingZone>
        </ContentView>
    );
}
